import React, {
  useState, useEffect, useRef,
} from 'react';
import { Typography, CircularProgress } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { OtpInputGroup } from './OtpInputGroup';

import { ErrorText, ResendButtonBox } from './OtpInputGroup.styles';

import Button from '@/components/Button';

const DEFAULT_COOLDOWN_SECONDS = 30;

interface OtpWithResendProps {
  /** Called once all 6 digits are filled. */
  onComplete: (token: string) => void;
  /** Called when the user requests a resend. Should throw on failure. */
  onResend: () => Promise<void>;
  /** Called on every keystroke — use to clear parent error state or track analytics. */
  onChange?: (value: string) => void;
  /** Error text from the parent (e.g. invalid token, activation failure). Inputs are
   * auto-reset whenever this transitions from absent to present. */
  errorText?: string;
  /** Set true while the parent is verifying — disables all inputs. */
  disabled?: boolean;
  /** Override the initial resend cooldown (seconds). Defaults to 30. */
  cooldownSeconds?: number;
  /** Extra inline styles for the resend button (e.g. outlined Spark theme). */
  resendButtonStyle?: React.CSSProperties;
}

export const OtpWithResend: React.FC<OtpWithResendProps> = ({
  onComplete,
  onResend,
  onChange,
  errorText,
  disabled = false,
  cooldownSeconds = DEFAULT_COOLDOWN_SECONDS,
  resendButtonStyle,
}) => {
  const { t } = useTranslation();
  const [resetCount, setResetCount] = useState(0);
  const [cooldown, setCooldown] = useState(cooldownSeconds);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const prevErrorTextRef = useRef<string | undefined>(undefined);

  // Auto-reset inputs when errorText transitions from absent to present.
  useEffect(() => {
    if (!prevErrorTextRef.current && errorText) {
      setResetCount((c) => c + 1);
    }
    prevErrorTextRef.current = errorText;
  }, [errorText]);

  // Cooldown timer — ticks once per second.
  useEffect(() => {
    if (cooldown <= 0) { return undefined; }
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (isResending || cooldown > 0) { return; }
    setResendError('');
    setResendSuccess(false);
    setIsResending(true);
    try {
      await onResend();
      setResendSuccess(true);
      setResetCount((c) => c + 1);
      setCooldown(cooldownSeconds);
    } catch {
      setResendError(t('coverage.pre_onboarding.sms_verify.resend_error'));
    } finally {
      setIsResending(false);
    }
  };

  const handleChange = (value: string) => {
    if (resendError) { setResendError(''); }
    onChange?.(value);
  };

  const displayError = errorText || resendError;

  return (
    <>
      <OtpInputGroup
        onComplete={onComplete}
        onChange={handleChange}
        hasError={!!displayError}
        disabled={disabled}
        resetCount={resetCount}
      />

      {displayError && (
        <ErrorText data-testid="error-message">{displayError}</ErrorText>
      )}

      <ResendButtonBox>
        <Button
          variant="contained"
          data-testid="resend-code-button"
          type="button"
          fullWidth
          rounded
          style={resendButtonStyle}
          onClick={handleResend}
          disabled={cooldown > 0 || isResending}
        >
          {isResending && (
            <>
              <CircularProgress size={14} color="inherit" style={{ marginRight: 6 }} />
              {t('coverage.pre_onboarding.sms_verify.sending')}
            </>
          )}
          {!isResending && (
            cooldown > 0
              ? `${t('coverage.pre_onboarding.sms_verify.resend_code')} (${cooldown}s)`
              : t('coverage.pre_onboarding.sms_verify.resend_code')
          )}
        </Button>

        {resendSuccess && (
          <Typography
            color="textPrimary"
            data-testid="resend-success-message"
            style={{ marginTop: 4, fontSize: 14 }}
          >
            {t('coverage.pre_onboarding.sms_verify.resend_success')}
          </Typography>
        )}
      </ResendButtonBox>
    </>
  );
};
